import { Link, createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { AngaMark } from "@/components/brand/AngaMark";
import { Button } from "@/components/ui/button";
import { ApiError, authApi } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Reset password" },
      { name: "description", content: "Request a password reset link for your resident account." },
    ],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { platformName } = usePlatformConfig();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.forgotPassword(email.trim());
      setSent(true);
      setDevResetUrl(result.devResetUrl ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.errorGeneric"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_55%)]" />
      <div className="relative w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm md:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#064E3B] text-white">
            <AngaMark className="h-6 w-6" title={platformName} />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-none">{platformName}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              {t("auth.forgotPassword")}
            </p>
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold">{t("auth.forgotTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("auth.forgotBody")}</p>

        {sent ? (
          <div className="mt-6 space-y-4">
            <p className="rounded-md border border-primary/20 bg-primary-soft px-3 py-2 text-sm">
              {t("auth.forgotSent")}
            </p>
            {devResetUrl ? (
              <p className="text-xs text-muted-foreground">
                Development reset link:{" "}
                <a className="font-semibold text-primary underline" href={devResetUrl}>
                  open it
                </a>
              </p>
            ) : null}
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <label className="block text-xs font-semibold">
              {t("common.email")}
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                autoComplete="email"
              />
            </label>
            {error ? (
              <p className="rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
                {error}
              </p>
            ) : null}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("common.loading") : t("auth.sendReset")}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">
            {t("auth.backToLogin")}
          </Link>
        </p>
      </div>
    </div>
  );
}
